import { useEffect, useMemo, useState } from 'react';
import { Modal } from '../components/Modal';
import { PandaDocEmbed } from '../embeds/PandaDocEmbed';
import type { FormInput, MutationResult, VerifyResult } from '../api/client';
import type { FormItem, FormStatus, PublicForm, SectionInfo } from '../types';
import { useAdmin } from './context';

interface Props {
  /** null = create mode */
  form: FormItem | null;
  sections: SectionInfo[];
  /** set when a section was just created from within this editor — preselects it */
  newSectionKey: string | null;
  onNewSection: () => void;
  onClose: () => void;
  onSaved: (result: MutationResult) => void;
}

type VerifyState =
  | { state: 'idle' }
  | { state: 'checking' }
  | { state: 'done'; result: VerifyResult };

const inputCls =
  'w-full rounded-xl border border-border bg-input px-3.5 py-2.5 text-sm outline-none transition-colors placeholder:text-muted/70 focus:border-brand';
const labelCls = 'mb-1.5 block text-xs font-semibold uppercase tracking-wide text-muted';

const NEW_SECTION_VALUE = '__new__';

export function FormEditor({ form, sections, newSectionKey, onNewSection, onClose, onSaved }: Props) {
  const { api } = useAdmin();
  const isEdit = form !== null;

  const [section, setSection] = useState(form?.section ?? sections[0]?.key ?? '');
  const [name, setName] = useState(form?.name ?? '');
  const [displayName, setDisplayName] = useState(form?.displayName ?? '');
  const [description, setDescription] = useState(form?.description ?? '');
  const [embedUrl, setEmbedUrl] = useState(form?.embedUrl ?? '');
  const [status, setStatus] = useState<FormStatus>(form?.status ?? 'draft');
  const [height, setHeight] = useState<string>(form?.config?.height ? String(form.config.height) : '');
  const [verify, setVerify] = useState<VerifyState>({ state: 'idle' });
  const [showPreview, setShowPreview] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Preselect a section that was just created from within this editor.
  useEffect(() => {
    if (newSectionKey) setSection(newSectionKey);
  }, [newSectionKey]);

  // If the sections list finishes loading after mount, select the first one.
  useEffect(() => {
    if (!section && sections.length > 0) setSection(sections[0]!.key);
  }, [sections, section]);

  const slugPreview = useMemo(() => {
    const n = name.trim().toLowerCase().replace(/[^a-z0-9-]+/g, '-').replace(/^-+|-+$/g, '');
    return n ? `${section}/${n}` : `${section || '…'}/…`;
  }, [section, name]);

  const previewForm: PublicForm | null = useMemo(() => {
    try {
      const u = new URL(embedUrl);
      if (u.protocol !== 'https:' || !(u.hostname === 'pandadoc.com' || u.hostname.endsWith('.pandadoc.com'))) {
        return null;
      }
      return {
        slug: 'preview/preview',
        name: 'preview',
        displayName: displayName || 'Preview',
        provider: 'pandadoc',
        embedUrl: u.href,
        ...(height ? { config: { height: Number(height) } } : {}),
      };
    } catch {
      return null;
    }
  }, [embedUrl, displayName, height]);

  const runVerify = async () => {
    setVerify({ state: 'checking' });
    try {
      const result = await api.verifyLink(embedUrl);
      setVerify({ state: 'done', result });
    } catch (err) {
      setVerify({
        state: 'done',
        result: { ok: false, error: err instanceof Error ? err.message : 'Check failed' },
      });
    }
  };

  const save = async () => {
    setSaving(true);
    setError(null);
    const payload: FormInput = {
      displayName: displayName.trim(),
      description: description.trim() ? description.trim() : null,
      embedUrl: embedUrl.trim(),
      status,
      config: height ? { height: Number(height) } : null,
    };
    try {
      const result = isEdit
        ? await api.updateForm(form.slug, payload)
        : await api.createForm({ ...payload, section, name: slugPreview.split('/')[1] });
      onSaved(result);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Save failed');
      setSaving(false);
    }
  };

  const canSave =
    displayName.trim().length > 0 &&
    embedUrl.trim().length > 0 &&
    (isEdit || (section.length > 0 && !slugPreview.endsWith('/…'))) &&
    !saving;

  return (
    <Modal title={isEdit ? `Edit ${form.displayName}` : 'New form'} onClose={onClose} wide={showPreview}>
      <div className="flex flex-col gap-5">
        {!isEdit && (
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label htmlFor="fe-section" className={labelCls}>Section</label>
              <select
                id="fe-section"
                value={section}
                onChange={(e) => {
                  if (e.target.value === NEW_SECTION_VALUE) onNewSection();
                  else setSection(e.target.value);
                }}
                className={inputCls}
              >
                {sections.map((s) => (
                  <option key={s.key} value={s.key}>{s.label}</option>
                ))}
                <option value={NEW_SECTION_VALUE}>＋ New section…</option>
              </select>
            </div>
            <div>
              <label htmlFor="fe-name" className={labelCls}>URL name</label>
              <input
                id="fe-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="new-hire"
                className={inputCls}
                autoComplete="off"
              />
            </div>
          </div>
        )}

        {!isEdit && (
          <p className="rounded-xl border border-border bg-input/50 px-3.5 py-2.5 text-xs text-muted">
            Public URL: <code className="font-mono text-foreground">forms.knocking.com/{slugPreview}</code>{' '}
            — fixed after creation.
          </p>
        )}

        <div>
          <label htmlFor="fe-display" className={labelCls}>Display name</label>
          <input
            id="fe-display"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            placeholder="05 New Hire Requisition"
            maxLength={120}
            className={inputCls}
          />
        </div>

        <div>
          <label htmlFor="fe-desc" className={labelCls}>Description <span className="font-normal normal-case">(optional)</span></label>
          <textarea
            id="fe-desc"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={2}
            maxLength={500}
            placeholder="Shown under the form title and on the section page."
            className={`${inputCls} resize-y`}
          />
        </div>

        <div>
          <label htmlFor="fe-url" className={labelCls}>PandaDoc form link</label>
          <div className="flex gap-2">
            <input
              id="fe-url"
              value={embedUrl}
              onChange={(e) => {
                setEmbedUrl(e.target.value);
                setVerify({ state: 'idle' });
              }}
              placeholder="https://form.pandadoc.com/form/… or https://eform.pandadoc.com/?eform=…"
              className={inputCls}
              inputMode="url"
              autoComplete="off"
              spellCheck={false}
            />
            <button
              type="button"
              onClick={() => void runVerify()}
              disabled={!embedUrl.trim() || verify.state === 'checking'}
              className="shrink-0 rounded-xl border border-border px-4 text-sm font-medium transition-colors hover:bg-input disabled:opacity-50"
            >
              {verify.state === 'checking' ? 'Checking…' : 'Verify link'}
            </button>
          </div>
          {verify.state === 'done' && (
            <p
              role="status"
              className={`mt-2 rounded-lg px-3 py-2 text-xs font-medium ${
                verify.result.ok ? 'bg-ok/10 text-ok' : 'bg-danger/10 text-danger'
              }`}
            >
              {verify.result.ok
                ? `Link works${verify.result.status ? ` (HTTP ${verify.result.status})` : ''} — the form will load for visitors.`
                : `Link check failed: ${verify.result.error ?? 'unknown error'}`}
            </p>
          )}
          <p className="mt-1.5 text-xs text-muted">
            Only <code className="font-mono">*.pandadoc.com</code> links are accepted. The form is embedded on
            forms.knocking.com — visitors never see this URL.
          </p>
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <label htmlFor="fe-status" className={labelCls}>Status</label>
            <select id="fe-status" value={status} onChange={(e) => setStatus(e.target.value as FormStatus)} className={inputCls}>
              <option value="draft">Draft — hidden from the public site</option>
              <option value="published">Published — live on the public site</option>
              <option value="archived">Archived — retired, shows a friendly 404</option>
            </select>
          </div>
          <div>
            <label htmlFor="fe-height" className={labelCls}>Embed height <span className="font-normal normal-case">(px, optional)</span></label>
            <input
              id="fe-height"
              value={height}
              onChange={(e) => setHeight(e.target.value.replace(/[^0-9]/g, ''))}
              placeholder="800"
              inputMode="numeric"
              className={inputCls}
            />
          </div>
        </div>

        {error && (
          <p role="alert" className="rounded-xl border border-danger/40 bg-danger/10 px-4 py-2.5 text-sm text-danger">
            {error}
          </p>
        )}

        <div className="flex flex-wrap items-center justify-between gap-3 border-t border-border pt-4">
          <button
            type="button"
            onClick={() => setShowPreview((v) => !v)}
            disabled={!previewForm}
            className="rounded-full border border-border px-4 py-2 text-sm font-medium transition-colors hover:bg-input disabled:opacity-50"
          >
            {showPreview ? 'Hide preview' : 'Preview embed'}
          </button>
          <div className="flex gap-3">
            <button
              type="button"
              onClick={onClose}
              className="rounded-full border border-border px-4 py-2 text-sm font-medium transition-colors hover:bg-input"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={() => void save()}
              disabled={!canSave}
              className="rounded-full bg-brand px-5 py-2 text-sm font-semibold text-brand-contrast transition-colors hover:bg-brand-strong disabled:opacity-50"
            >
              {saving ? 'Saving…' : isEdit ? 'Save changes' : 'Create form'}
            </button>
          </div>
        </div>

        {showPreview && previewForm && (
          <div className="border-t border-border pt-4">
            <p className="mb-3 text-xs font-semibold uppercase tracking-wide text-muted">
              Preview — exactly what visitors will see embedded
            </p>
            <PandaDocEmbed form={previewForm} />
          </div>
        )}
      </div>
    </Modal>
  );
}
